import React from 'react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router'

import {
  PracticianEntityType,
  PracticianSearchEntitySection,
  typeToSection,
  usePracticianFinder,
} from '../api/get-practician-finder-data'

import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { paths } from '@/config/path'
import { ORDER_STATUS_LABELS } from '@/features/orders/utils/type-enum-mapper'
import useDebounce from '@/hooks/use-debounce'
import { useDisclosure } from '@/hooks/use-disclosure'

export const PracticianFinder = () => {
  const [query, setQuery] = React.useState<string | undefined>()
  const debouncedQuery = useDebounce(query, 800)
  const finderQuery = usePracticianFinder({ query })
  const { isOpen, close, open } = useDisclosure()

  return (
    <div className="flex flex-col items-center gap-2 w-full relative">
      <SearchInput query={debouncedQuery} onChange={setQuery} onFocus={open} />
      {isOpen && query !== undefined && query.length > 0 ? (
        <SearchResults
          data={finderQuery.data}
          isLoading={!!query && finderQuery.isPending}
          error={finderQuery.error}
          onClose={close}
        />
      ) : null}
    </div>
  )
}

type SearchInputProps = {
  query: string | undefined
  onChange: (query: string) => void
  onFocus?: () => void
}
const SearchInput = ({ query, onChange, onFocus }: SearchInputProps) => {
  return (
    <div className="relative w-full">
      <Search className="absolute left-2.5 top-3 h-4 w-4 text-muted-foreground" />
      <Input
        type="search"
        placeholder="Rechercher: service, commande en cours..."
        className="w-full appearance-none bg-background pl-8 shadow-none"
        defaultValue={query}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
      />
    </div>
  )
}

type SearchResultsProps = {
  data: PracticianSearchEntitySection | undefined
  isLoading: boolean
  error: Error | null
  onClose?: () => void
}
const SearchResults = ({ data, isLoading, error, onClose }: SearchResultsProps) => {
  const navigate = useNavigate()
  const ref = React.useRef<HTMLUListElement>(null)

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        if (onClose) onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose])

  const handleItemClick = (e: React.MouseEvent, type: PracticianEntityType, id: string, name: string) => {
    e.preventDefault()
    switch (type) {
      case 'order.technician':
        navigate(paths.app.order.getHref(id))
        break
      case 'service':
        navigate(paths.app['search-service'].getHref({ query: name }))
        break
      default:
        break
    }

    if (onClose) onClose()
  }

  if (isLoading) {
    return (
      <div className="flex flex-col overflow-y-auto max-h-96 w-full bg-muted rounded-md absolute top-full left-0 z-10 border border-border">
        <Skeleton className="w-full h-20" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex flex-col overflow-y-auto max-h-96 w-full bg-muted rounded-md absolute top-full left-0 z-10 border border-border">
        <span className="p-4">Aucun résultat</span>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <span>Erreur</span>
      </div>
    )
  }

  return (
    <ul
      ref={ref}
      className="flex flex-col px-2 overflow-y-auto max-h-96 w-full bg-background rounded-md absolute top-full left-0 z-10 border border-border"
    >
      {Object.entries(data).map(([type, entities]) => {
        return (
          <SearchResultSection key={type} title={typeToSection(type)}>
            {entities.map((se) => (
              <SearchResultItem
                key={se.id}
                id={se.id}
                type={type as PracticianEntityType}
                title={se.name}
                subtitle={se.type === 'order.technician' ? ORDER_STATUS_LABELS[se.order_status] : ''}
                handleItemClick={handleItemClick}
              />
            ))}
          </SearchResultSection>
        )
      })}
    </ul>
  )
}

const SearchResultSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <>
      <li className="mt-2">
        <span className="px-2 text-xs text-accent-foreground/50">{title}</span>
        <ul>{children}</ul>
      </li>
      <Separator />
    </>
  )
}

type SearchResultItemProps = {
  id: string
  type: PracticianEntityType
  title: string
  subtitle?: string
  handleItemClick: (e: React.MouseEvent, type: PracticianEntityType, id: string, name: string) => void
}
const SearchResultItem = ({ id, type, title, subtitle, handleItemClick }: SearchResultItemProps) => {
  return (
    <li
      className="flex flex-row items-center justify-between p-2 hover:bg-muted-foreground/5 hover:rounded-md cursor-pointer"
      onClick={(e) => handleItemClick(e, type, id, title)}
    >
      <span className="truncate text-sm">{title}</span>
      {subtitle !== undefined && subtitle.length > 0 ? (
        <Badge className="text-xs" variant="outline">
          {subtitle}
        </Badge>
      ) : null}
    </li>
  )
}
